import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { motion, useReducedMotion } from 'framer-motion'
import { ArrowRight, Sparkles } from 'lucide-react'
import { useSiteContent } from '@/lib/site-content'
import LinkButton from '@/components/controls/link-button'
import CmsText from '@/components/generals/cms-text'
import FloatingBlob from '@/components/motion/floating-blob'
import { Stagger, StaggerItem } from '@/components/motion/fade-in'

const Hero: FC = (): ReactNode => {
    const { settings } = useSiteContent()
    const reduce = useReducedMotion()

    return (
        <section id='top' className='relative overflow-hidden bg-brand-cream pt-24 md:pt-28'>
            <FloatingBlob
                className='-left-32 top-10 h-[420px] w-[420px] text-brand-yellow-soft/60'
                amplitude={14}
                duration={10}
            />

            <FloatingBlob
                className='-right-40 -top-10 h-[520px] w-[520px] text-brand-blob-orange/55'
                amplitude={18}
                duration={12}
                delay={1.5}
            />

            <FloatingBlob
                className='right-1/3 top-44 h-[120px] w-[120px] text-brand-yellow/35'
                amplitude={8}
                duration={7}
                delay={0.8}
            />

            <div
                aria-hidden
                className='dot-cluster absolute right-12 top-24 hidden h-16 w-24 opacity-60 md:block'
            />

            <div className='container-page relative grid grid-cols-1 items-center gap-10 pb-20 pt-10 md:grid-cols-12 md:gap-6 md:pb-28'>
                <div className='md:col-span-7'>
                    <Stagger>
                        <StaggerItem>
                            <span className='inline-flex items-center gap-2 rounded-pill bg-white/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-brand-red shadow-[inset_0_0_0_1px_rgba(217,68,67,0.18)]'>
                                <Sparkles aria-hidden className='h-3.5 w-3.5' />
                                <span data-mdlfy-cms={`site_settings:${settings.id}:eid_name`}>
                                    {settings.eid_name}
                                </span>
                            </span>
                        </StaggerItem>

                        <StaggerItem>
                            <h1 className='mt-6 font-display text-5xl font-extrabold leading-[1.05] text-brand-red md:text-7xl'>
                                <CmsText textKey='hero.titleTop' className='block' />
                                <CmsText textKey='hero.titleBottom' className='block text-neutral-900' />
                            </h1>
                        </StaggerItem>

                        <StaggerItem>
                            <CmsText
                                as='p'
                                textKey='hero.subtitle'
                                className='mt-6 max-w-xl text-base leading-relaxed text-neutral-700 md:text-lg'
                            />
                        </StaggerItem>

                        <StaggerItem>
                            <div className='mt-8 flex flex-wrap gap-3'>
                                <LinkButton href='#porosit' variant='red' size='lg'>
                                    <CmsText textKey='hero.ctaPrimary' />
                                    <ArrowRight aria-hidden className='h-4 w-4' />
                                </LinkButton>

                                <LinkButton href='#rreth' variant='yellow' size='lg'>
                                    <CmsText textKey='hero.ctaSecondary' />
                                </LinkButton>
                            </div>
                        </StaggerItem>
                    </Stagger>
                </div>

                <div className='relative md:col-span-5'>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.94 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                        className='relative mx-auto aspect-square w-full max-w-md'
                    >
                        <svg
                            aria-hidden
                            viewBox='0 0 200 200'
                            className='absolute inset-0 h-full w-full text-brand-blob-orange/85'
                        >
                            <path
                                fill='currentColor'
                                d='M44.3,-58.6C58.5,-49.9,72,-37.5,76.3,-22.4C80.5,-7.4,75.6,10.4,66.5,24.7C57.4,39,44.2,49.7,29.3,57.5C14.4,65.4,-2.3,70.3,-17.9,67.5C-33.5,64.7,-48.1,54.1,-58.5,40.4C-69,26.7,-75.3,9.7,-73.4,-6C-71.5,-21.6,-61.4,-35.8,-48.5,-45.3C-35.6,-54.7,-19.9,-59.4,-3.4,-55.2C13.1,-50.9,30.1,-67.3,44.3,-58.6Z'
                                transform='translate(100 100)'
                            />
                        </svg>

                        <motion.div
                            animate={reduce ? undefined : { y: [0, -8, 0] }}
                            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                            className='absolute inset-0 flex items-center justify-center'
                        >
                            <Image
                                src='/images/dhurata-banner.png'
                                alt="Fëmijë të lumtur me tullumbace duke festuar Bajramin"
                                width={760}
                                height={760}
                                preload
                                className='h-auto w-full max-w-md select-none drop-shadow-[0_24px_36px_rgba(217,68,67,0.18)]'
                            />
                        </motion.div>
                    </motion.div>
                </div>
            </div>

            <svg
                aria-hidden
                viewBox='0 0 1440 80'
                preserveAspectRatio='none'
                className='block h-12 w-full text-brand-red md:h-20'
            >
                <path
                    fill='currentColor'
                    d='M0,40 C240,80 480,0 720,30 C960,60 1200,90 1440,40 L1440,80 L0,80 Z'
                />
            </svg>
        </section>
    )
}

export default Hero
